// Optimistic concurrency control — frontend helpers (finding M3, Wave 6b).
//
// The backend now versions `productions`, `items` and `saved_views`. Every PUT
// to those resources must echo back the integer `version` the client last read
// (from the GET that populated the edit form) in an `If-Match` request header.
//
//   GET resource  →  resource.version  →  form holds it  →  PUT If-Match: <version>
//
// Backend responses we must handle:
//   • 428 PRECONDITION_REQUIRED  — client forgot to send If-Match (frontend bug)
//   • 400 INVALID_PRECONDITION   — If-Match malformed (frontend bug)
//   • 409 VERSION_CONFLICT       — someone else saved first; our save did NOT apply
//   • 200                        — success; body carries the incremented `version`
//
// This module keeps the wiring in one place so the three resource layers
// (useItems, useProductions, savedViewsStore) stay consistent.

/**
 * Build the request options object carrying the `If-Match` precondition header.
 *
 * The header value is the raw integer version as a string (e.g. `If-Match: 4`).
 * When `version` is null/undefined we deliberately omit the header — the backend
 * will reply 428 and {@link isPreconditionError} will surface it as a generic
 * save failure. We never fabricate a version: a missing one is a real bug and
 * silently sending `If-Match: 0` would mask it.
 *
 * @param {number|null|undefined} version  Last-read resource version.
 * @param {object} [extraOptions]          Additional api.js request options to merge.
 * @returns {object} Options object for api.put(url, body, options).
 */
export function withIfMatch(version, extraOptions = {}) {
  const headers = { ...(extraOptions.headers || {}) }
  if (version !== undefined && version !== null && version !== '') {
    // Raw integer per the backend contract — not an ETag-style quoted string.
    headers['If-Match'] = String(version)
  }
  return { ...extraOptions, headers }
}

/** HTTP status of an api.js error, or undefined for network/timeout errors. */
function statusOf(err) {
  return err?.response?.status
}

/** Backend error `code` discriminator, if the server returned a JSON body. */
function codeOf(err) {
  return err?.response?.data?.code
}

/**
 * True when the error is a 409 optimistic-concurrency conflict — another user
 * saved the same record first, so the current save did NOT apply and the user
 * must reload to see the latest version.
 *
 * @param {unknown} err  An api.js error object.
 * @returns {boolean}
 */
export function isVersionConflict(err) {
  return statusOf(err) === 409 && codeOf(err) === 'VERSION_CONFLICT'
}

/**
 * True when the error is a 428/400 precondition failure — the client failed to
 * send (428) or sent a malformed (400) `If-Match` header. These indicate a
 * frontend bug and should not occur once `withIfMatch` is wired correctly;
 * callers surface them as a generic save error rather than crashing.
 *
 * @param {unknown} err  An api.js error object.
 * @returns {boolean}
 */
export function isPreconditionError(err) {
  const code = codeOf(err)
  return (
    (statusOf(err) === 428 && code === 'PRECONDITION_REQUIRED') ||
    (statusOf(err) === 400 && code === 'INVALID_PRECONDITION')
  )
}

/**
 * The server's authoritative current version on a 409 conflict, when known.
 * The backend includes `current_version` (int or null) in the 409 body so the
 * client could re-seed a retry; we mainly expose it for diagnostics/tests.
 *
 * @param {unknown} err  An api.js error object.
 * @returns {number|null|undefined}
 */
export function conflictCurrentVersion(err) {
  return err?.response?.data?.current_version
}
